import { initContract } from "@ts-rest/core";
import { z } from "zod";

import {
  CompanyGetAllCompaniesResponseType,
  CompanyGetCompanyResponseType,
  CompanyGetPatentsResponseType,
  CompanyGetSourcesResponseType,
} from "@src/resources/companies/companies.service";

const c = initContract();

const GridSortDirectionSchema = z.union([z.literal("asc"), z.literal("desc"), z.literal(null), z.undefined()]);

// GridSortItem schema
const GridSortItemSchema = z.object({
  field: z.string(),
  sort: GridSortDirectionSchema,
});

// Main schema
const stringToBoolean = (value: string): boolean => {
  if (value === "true") return true;
  if (value === "false") return false;
};

const getAllCompaniesSchema = z.object({
  autm2023: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  autm2024: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  bio2022: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  bio2023: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  bio2024: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  companyIds: z.array(z.string()).optional(),
  contactIds: z.array(z.string()).optional(),
  forProfit: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  govLicense: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  hasAttendedConference: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  hasVerifiedEmail: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  industries: z.array(z.string()).optional(),
  jobTitles: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  les2023: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  licensed: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  locations: z.array(z.string()).optional(),
  maxEmployees: z.number({ coerce: true }).optional(),
  minEmployees: z.number({ coerce: true }).optional(),
  nonProfit: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  page: z.number({ coerce: true }).optional(),
  pageSize: z.number({ coerce: true }),
  sbir: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  search: z.string().optional(),
  sort: z.array(GridSortItemSchema),
  sponsored: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
  uidp2023: z.union([z.boolean(), z.string()]).transform(stringToBoolean).optional(),
});

export type GetAllCompaniesSchemaType = z.infer<typeof getAllCompaniesSchema>;

const index = c.router({
  getAllCompanies: {
    method: "GET",
    path: "/company/get-all-companies",
    query: getAllCompaniesSchema,
    responses: {
      200: c.type<CompanyGetAllCompaniesResponseType>(),
    },
  },
  getCompany: {
    method: "GET",
    path: "/companies/:id",
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: c.type<CompanyGetCompanyResponseType>(),
    },
  },
  getPatents: {
    method: "GET",
    path: "/companies/get-patents/:companyId",
    pathParams: z.object({
      companyId: z.string(),
    }),
    query: z.object({
      field: z.string().optional(),
      limit: z.number({ coerce: true }),
      page: z.number({ coerce: true }),
      patentStatus: z.array(z.enum(["Application", "Granted"])),
      search: z.string().optional(),
      searchId: z.string().optional(),
      sort: z.string().optional(),
      technologyId: z.string().optional(),
    }),
    responses: {
      200: c.type<CompanyGetPatentsResponseType>(),
    },
  },
  getSources: {
    method: "GET",
    path: "/companies/get-sources/:id",
    pathParams: z.object({
      id: z.string(),
    }),
    responses: {
      200: c.type<CompanyGetSourcesResponseType>(),
    },
  },
});

export default index;
